import { useEffect, useState } from "react";
import { PageHeader, Section } from "@/components/layout/Section";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function Testimonies() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/testimonies").then((r) => setItems(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData(e.target);
    try {
      await api.post("/testimonies", {
        name: fd.get("name"),
        email: fd.get("email") || null,
        location: fd.get("location") || null,
        content: fd.get("content"),
      });
      toast.success("Thank you! Your testimony is submitted for review.");
      e.target.reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="testimonies-page">
      <PageHeader
        kicker="Testimonies"
        title="What God has done."
        subtitle="Real people. Real miracles. Real Jesus."
      />

      <Section>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <motion.blockquote
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              key={t.id}
              className="card-soft p-8"
              data-testid={`testimony-card-${i}`}
            >
              <div className="text-gold text-5xl font-serif leading-none">"</div>
              <p className="mt-2 text-slate-700 leading-relaxed">{t.content}</p>
              <footer className="mt-6">
                <div className="font-semibold text-royal">{t.name}</div>
                {t.location && <div className="text-xs text-slate-500">{t.location}</div>}
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </Section>

      <Section id="share" className="bg-royal-800 text-white rounded-[3rem] mx-4" testId="share-testimony">
        <div className="max-w-2xl mx-auto">
          <div className="kicker text-gold mb-4">Share Your Testimony</div>
          <h2 className="font-serif text-4xl">Testify to His goodness.</h2>
          <p className="mt-4 text-white/70">Your story will strengthen someone's faith. Share what Jesus has done for you.</p>

          <form onSubmit={submit} className="mt-10 grid gap-5" data-testid="testimony-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-white/80">Your name</Label>
                <Input name="name" required className="mt-2 bg-white/10 border-white/20 text-white placeholder-white/40" data-testid="testimony-name" />
              </div>
              <div>
                <Label className="text-white/80">Location (optional)</Label>
                <Input name="location" className="mt-2 bg-white/10 border-white/20 text-white placeholder-white/40" data-testid="testimony-location" />
              </div>
            </div>
            <div>
              <Label className="text-white/80">Email (optional)</Label>
              <Input name="email" type="email" className="mt-2 bg-white/10 border-white/20 text-white placeholder-white/40" data-testid="testimony-email" />
            </div>
            <div>
              <Label className="text-white/80">Your testimony</Label>
              <Textarea name="content" required rows={6} className="mt-2 bg-white/10 border-white/20 text-white placeholder-white/40" data-testid="testimony-content" />
            </div>
            <button disabled={busy} className="btn-primary w-fit" data-testid="testimony-submit">
              Share Testimony
            </button>
          </form>
        </div>
      </Section>
    </div>
  );
}
